/* eslint-disable @typescript-eslint/no-explicit-any */
import { GlobalCurrencyFilterDTO } from '@/@dtos/GlobalSearchDTO';
import { ToastOptions, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getBrandListById } from '@/CommonApis/BrandListById';
import { ResponseDTO } from '@/@dtos/ResponseDTO';
import JsFileDownloader from 'js-file-downloader';
import { removeEmpty } from '@/utils/removeEmpty';
import { clearPendingChatLink, getPendingChatLink } from '@/utils/chatLinkState';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { isMockAuthEnabled } from '@/config/authMode';

type ApiErrorMessage = string | { [key: string]: string[] };

export class ApiProvider {
  static apiUrl = import.meta.env.VITE_API_URL;

  static token = localStorage.getItem('access_token');
  private static readonly SESSION_EXPIRED_MESSAGE_KEY = 'session_expired_message';
  private static readonly SESSION_EXPIRED_MESSAGE_TEXT =
    'Your session has expired. Please log in again.';
  private static readonly SESSION_PROBE_INTERVAL_MS = 15000;
  private static isSessionRedirectInProgress = false;
  private static lastSessionProbeAt = 0;
  private static sessionProbeInFlight: Promise<boolean> | null = null;
  private static authTokenFetchInFlight: Promise<string | null> | null = null;

  private static isAuthDebugEnabled(): boolean {
    try {
      const explicitFlag =
        localStorage.getItem('auth_debug') || sessionStorage.getItem('auth_debug');
      if (explicitFlag === '1' || explicitFlag === 'true') {
        return true;
      }
    } catch {
      // Ignore storage failures.
    }

    if (typeof window === 'undefined') {
      return false;
    }

    const host = window.location.hostname || '';
    return host.includes('localhost') || host.includes('127.0.0.1') || host.includes('dev-engine');
  }

  private static authDebug(message: string, meta?: Record<string, any>): void {
    if (!ApiProvider.isAuthDebugEnabled()) return;

    const timestamp = new Date().toISOString();
    if (meta) {
      console.debug(`[AUTH-DEBUG ${timestamp}] ${message}`, ApiProvider.toDebugSnapshot(meta));
    } else {
      console.debug(`[AUTH-DEBUG ${timestamp}] ${message}`);
    }
  }

  private static toDebugSnapshot(value: any, depth = 4): any {
    if (depth <= 0) {
      return '[max-depth]';
    }

    if (value === null || value === undefined) {
      return value;
    }

    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      return value;
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
      };
    }

    if (Array.isArray(value)) {
      const maxItems = 30;
      return {
        length: value.length,
        items: value.slice(0, maxItems).map((item) => ApiProvider.toDebugSnapshot(item, depth - 1)),
        truncated: value.length > maxItems,
      };
    }

    if (t === 'object') {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) {
        out[k] = ApiProvider.toDebugSnapshot(v, depth - 1);
      }
      return out;
    }

    return String(value);
  }

  private static amplifyStorageDebugMeta(): Record<string, any> {
    try {
      const clientId = String(import.meta.env.VITE_COGNITO_CLIENT_ID || '').trim();
      const isCognitoKey = (key: string) =>
        key.includes('CognitoIdentityServiceProvider') ||
        (clientId !== '' && key.includes(clientId));

      const allKeys = Object.keys(localStorage).sort();
      const cognitoKeys = allKeys.filter(isCognitoKey);
      const lowerKeys = cognitoKeys.map((key) => key.toLowerCase());
      const findKey = (needle: string) => lowerKeys.some((key) => key.includes(needle));

      // Also inspect sessionStorage so we can see where Amplify actually stored the tokens.
      let ssResult: Record<string, any> = { error: 'unavailable' };
      try {
        const ssAllKeys = Object.keys(sessionStorage).sort();
        const ssCognitoKeys = ssAllKeys.filter(isCognitoKey);
        const ssLower = ssCognitoKeys.map((k) => k.toLowerCase());
        const findSsKey = (needle: string) => ssLower.some((k) => k.includes(needle));
        ssResult = {
          totalSessionStorageKeys: ssAllKeys.length,
          cognitoKeyCount: ssCognitoKeys.length,
          hasLastAuthUserKey: findSsKey('lastauthuser'),
          hasRefreshTokenKey: findSsKey('refreshtoken'),
          hasAccessTokenKey: findSsKey('accesstoken'),
          hasIdTokenKey: findSsKey('idtoken'),
          sampleCognitoKeys: ssCognitoKeys.slice(0, 20),
        };
      } catch {
        /* ignore */
      }

      return {
        totalLocalStorageKeys: allKeys.length,
        cognitoKeyCount: cognitoKeys.length,
        hasLastAuthUserKey: findKey('lastauthuser'),
        hasRefreshTokenKey: findKey('refreshtoken'),
        hasAccessTokenKey: findKey('accesstoken'),
        hasIdTokenKey: findKey('idtoken'),
        sampleCognitoKeys: cognitoKeys.slice(0, 20),
        sessionStorage: ssResult,
      };
    } catch (error: any) {
      return {
        error: error?.message || 'storage-unavailable',
      };
    }
  }

  /**
   * Read a Cognito token-store value from wherever Amplify persisted it.
   * Amplify v6 defaults to localStorage but may fall back to sessionStorage in
   * some browser/extension environments, so we check both.
   */
  private static getCognitoStoreValue(key: string): string | null {
    try {
      const v = localStorage.getItem(key);
      if (v !== null) return v;
    } catch {
      /* ignore */
    }
    try {
      const v = sessionStorage.getItem(key);
      if (v !== null) return v;
    } catch {
      /* ignore */
    }
    return null;
  }

  private static setCognitoStoreValue(key: string, value: string): void {
    // Write to whichever store currently holds this key; fall back to localStorage.
    let wroteToSs = false;
    try {
      if (sessionStorage.getItem(key) !== null) {
        sessionStorage.setItem(key, value);
        wroteToSs = true;
      }
    } catch {
      /* ignore */
    }
    try {
      if (!wroteToSs || localStorage.getItem(key) !== null) {
        localStorage.setItem(key, value);
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * Refresh Cognito tokens by calling InitiateAuth REFRESH_TOKEN_AUTH directly.
   *
   * Background: Amplify v6.10+ switched its internal token-refresh path to the
   * newer GetTokensFromRefreshToken Cognito API. That API does NOT return an
   * IdToken for native (username/password) auth flows, so after every Amplify
   * refresh, storeTokens() deletes the stored IdToken. Our backend only accepts
   * IdTokens (token_use === 'id'), so we bypass Amplify and call the classic
   * InitiateAuth REFRESH_TOKEN_AUTH endpoint directly — this always returns both
   * AccessToken and IdToken.
   *
   * Returns the fresh IdToken string on success, or null on failure.
   */
  private static async refreshIdTokenViaInitiateAuth(): Promise<string | null> {
    const clientId = String(import.meta.env.VITE_COGNITO_CLIENT_ID || '').trim();
    const userPoolId = String(import.meta.env.VITE_COGNITO_USER_POOL_ID || '').trim();
    if (!clientId || !userPoolId) return null;

    const region = userPoolId.split('_')[0];
    if (!region || !region.includes('-')) return null;

    // Locate the last authenticated user from Amplify's storage.
    const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
    const username = ApiProvider.getCognitoStoreValue(lastAuthUserKey);
    if (!username) return null;

    // Locate the stored refresh token.
    const refreshTokenKey = `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`;
    const refreshToken = ApiProvider.getCognitoStoreValue(refreshTokenKey);
    if (!refreshToken) return null;

    ApiProvider.authDebug(
      'refreshIdTokenViaInitiateAuth(): calling InitiateAuth REFRESH_TOKEN_AUTH',
      {
        region,
        clientId,
        username,
      },
    );

    try {
      const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
          'Cache-Control': 'no-store',
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: clientId,
          AuthParameters: { REFRESH_TOKEN: refreshToken },
        }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        ApiProvider.authDebug(
          'refreshIdTokenViaInitiateAuth(): InitiateAuth returned non-OK status',
          {
            status: response.status,
            errorCode: (errBody as any)?.__type ?? null,
            message: (errBody as any)?.message ?? null,
          },
        );
        return null;
      }

      const data = await response.json();
      const idToken: string | undefined = (data as any)?.AuthenticationResult?.IdToken;
      const accessToken: string | undefined = (data as any)?.AuthenticationResult?.AccessToken;
      const newRefreshToken: string | undefined = (data as any)?.AuthenticationResult?.RefreshToken;

      ApiProvider.authDebug('refreshIdTokenViaInitiateAuth(): InitiateAuth succeeded', {
        idToken: ApiProvider.tokenDebugMeta(idToken),
        accessToken: ApiProvider.tokenDebugMeta(accessToken),
        hasNewRefreshToken: Boolean(newRefreshToken),
      });

      if (!idToken) return null;

      // Write the new tokens back into Amplify's storage so subsequent
      // fetchAuthSession() calls see fresh tokens.
      const idTokenKey = `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`;
      const accessTokenKey = `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`;

      ApiProvider.setCognitoStoreValue(idTokenKey, idToken);
      if (accessToken) ApiProvider.setCognitoStoreValue(accessTokenKey, accessToken);
      if (newRefreshToken) ApiProvider.setCognitoStoreValue(refreshTokenKey, newRefreshToken);

      try {
        localStorage.setItem('access_token', idToken);
      } catch {
        /* ignore */
      }

      return idToken;
    } catch (error: any) {
      ApiProvider.authDebug('refreshIdTokenViaInitiateAuth(): fetch threw', {
        name: error?.name,
        message: error?.message,
      });
      return null;
    }
  }

  private static amplifySessionDebugMeta(session: any): Record<string, any> {
    const idToken = session?.tokens?.idToken?.toString?.();
    const accessToken = session?.tokens?.accessToken?.toString?.();

    return {
      hasTokens: Boolean(session?.tokens),
      hasIdentityId: Boolean(session?.identityId),
      hasCredentials: Boolean(session?.credentials),
      idToken: ApiProvider.tokenDebugMeta(idToken),
      accessToken: ApiProvider.tokenDebugMeta(accessToken),
    };
  }

  private static tokenDebugMeta(token?: string | null): Record<string, any> {
    const payload = ApiProvider.decodeJwtPayloadSafe(token);
    const now = Math.floor(Date.now() / 1000);
    const exp = Number(payload?.exp ?? NaN);
    const iat = Number(payload?.iat ?? NaN);

    return {
      present: Boolean(token),
      length: token ? token.length : 0,
      exp: Number.isFinite(exp) ? exp : null,
      expInSec: Number.isFinite(exp) ? exp - now : null,
      iat: Number.isFinite(iat) ? iat : null,
      iatAgeSec: Number.isFinite(iat) ? now - iat : null,
      token_use: payload?.token_use ?? null,
      aud: payload?.aud ?? null,
      subSuffix: typeof payload?.sub === 'string' ? payload.sub.slice(-8) : null,
      cognitoUsername: payload?.['cognito:username'] ?? null,
      email: payload?.email ?? null,
    };
  }

  /**
   * Get a valid authentication token for API requests.
   *
   * Always fetches from Amplify so that expired Cognito IdTokens are
   * silently refreshed via the Cognito refresh token — users stay logged
   * in for the full refresh-token lifetime rather than only the 1-hour
   * IdToken TTL.
   *
   * In local mock mode (no real Cognito session) we fall back to whatever
   * is stored in localStorage.
   */
  static async getAuthToken(): Promise<string | null> {
    if (ApiProvider.authTokenFetchInFlight) {
      ApiProvider.authDebug('getAuthToken(): reusing in-flight token fetch');
      return ApiProvider.authTokenFetchInFlight;
    }

    ApiProvider.authDebug('getAuthToken(): starting token resolution', {
      hasLocalUserData: Boolean(localStorage.getItem('userData')),
      localStorageToken: ApiProvider.tokenDebugMeta(localStorage.getItem('access_token')),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    ApiProvider.authTokenFetchInFlight = ApiProvider.resolveAuthToken();
    try {
      const token = await ApiProvider.authTokenFetchInFlight;
      ApiProvider.authDebug('getAuthToken(): token resolution completed', {
        resolvedToken: ApiProvider.tokenDebugMeta(token),
      });
      return token;
    } finally {
      ApiProvider.authTokenFetchInFlight = null;
    }
  }

  private static async resolveAuthToken(): Promise<string | null> {
    // In local mock mode, there is no Cognito session to fall back to.
    if (isMockAuthEnabled) {
      ApiProvider.authDebug('resolveAuthToken(): mock mode fallback to localStorage token');
      return localStorage.getItem('access_token');
    }

    let idToken: string | undefined;

    // Step 1: plain Amplify session read (uses cached tokens; does NOT call Cognito).
    try {
      const session = await fetchAuthSession();
      idToken = session.tokens?.idToken?.toString();
      ApiProvider.authDebug('resolveAuthToken(): fetchAuthSession() succeeded', {
        session: ApiProvider.amplifySessionDebugMeta(session),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
    } catch (error: any) {
      ApiProvider.authDebug('resolveAuthToken(): fetchAuthSession() threw', {
        name: error?.name,
        message: error?.message,
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
    }

    // Step 2: if IdToken is missing or near-expiry, refresh via InitiateAuth
    // REFRESH_TOKEN_AUTH (the classic Cognito API that always returns IdToken).
    //
    // WHY NOT fetchAuthSession({ forceRefresh: true })?
    // Amplify v6.10+ switched its internal refresh path to the newer
    // GetTokensFromRefreshToken Cognito API. That API does NOT return an IdToken
    // for native (username/password) auth flows. Amplify's storeTokens() then
    // deletes the stored IdToken from storage, making every subsequent session
    // read return no IdToken — effectively logging the user out after 15 minutes.
    // The classic InitiateAuth REFRESH_TOKEN_AUTH always returns IdToken.
    if (!idToken || ApiProvider.isJwtExpiredOrNearExpiry(idToken, 45)) {
      ApiProvider.authDebug(
        'resolveAuthToken(): IdToken absent/near-expiry — refreshing via InitiateAuth',
        {
          currentToken: ApiProvider.tokenDebugMeta(idToken),
          storage: ApiProvider.amplifyStorageDebugMeta(),
        },
      );
      const refreshed = await ApiProvider.refreshIdTokenViaInitiateAuth();
      if (refreshed) {
        idToken = refreshed;
        ApiProvider.authDebug('resolveAuthToken(): InitiateAuth refresh succeeded', {
          idToken: ApiProvider.tokenDebugMeta(idToken),
        });
      } else {
        // InitiateAuth refresh unavailable (e.g. refresh token missing from storage,
        // SSO/OAuth flow, or network error). Fall through; the current idToken (if
        // still valid) or the localStorage fallback will be returned below.
        ApiProvider.authDebug('resolveAuthToken(): InitiateAuth refresh returned null', {
          storage: ApiProvider.amplifyStorageDebugMeta(),
        });
      }
    }

    if (idToken && !ApiProvider.isJwtExpiredOrNearExpiry(idToken, 5)) {
      // During fast account switching (e.g. SSO logout -> password login), Amplify
      // can briefly return a stale token from the previous user. If localStorage
      // already contains a newly established app session for a different user,
      // prefer the stored access token — but only if it is still valid.
      const localUser = ApiProvider.returnUserDataSafe();
      const localStoredToken = localStorage.getItem('access_token');
      const hasUsableLocalStoredToken = Boolean(
        localStoredToken && !ApiProvider.isJwtExpiredOrNearExpiry(localStoredToken, 5),
      );
      const amplifyTokenMatchesLocalUser = localUser
        ? ApiProvider.tokenMatchesUser(idToken, localUser)
        : true;
      const localStoredTokenMatchesLocalUser =
        localUser && localStoredToken
          ? ApiProvider.tokenMatchesUser(localStoredToken, localUser)
          : false;

      if (localUser && hasUsableLocalStoredToken && !amplifyTokenMatchesLocalUser) {
        if (localStoredTokenMatchesLocalUser) {
          ApiProvider.authDebug(
            'resolveAuthToken(): using localStorage token due to cross-user mismatch guard',
            {
              amplifyToken: ApiProvider.tokenDebugMeta(idToken),
              localStoredToken: ApiProvider.tokenDebugMeta(localStoredToken),
              amplifyTokenMatchesLocalUser,
              localStoredTokenMatchesLocalUser,
              localUserId: localUser?.id ?? null,
              localUserEmail: localUser?.email ?? null,
            },
          );
          return localStoredToken as string;
        }

        ApiProvider.authDebug(
          'resolveAuthToken(): both Amplify/local tokens mismatch current user; keeping Amplify token',
          {
            amplifyToken: ApiProvider.tokenDebugMeta(idToken),
            localStoredToken: ApiProvider.tokenDebugMeta(localStoredToken),
            amplifyTokenMatchesLocalUser,
            localStoredTokenMatchesLocalUser,
            localUserId: localUser?.id ?? null,
            localUserEmail: localUser?.email ?? null,
          },
        );
      }

      // Keep localStorage in sync with the fresh token.
      try {
        localStorage.setItem('access_token', idToken);
      } catch {
        // Ignore storage errors (e.g. private-browsing quota limits).
      }

      ApiProvider.authDebug('resolveAuthToken(): returning valid token', {
        idToken: ApiProvider.tokenDebugMeta(idToken),
      });
      return idToken;
    }

    // Last resort: a stored token may still be within its validity window.
    const storedToken = localStorage.getItem('access_token');
    if (ApiProvider.isJwtExpiredOrNearExpiry(storedToken, 5)) {
      ApiProvider.authDebug('resolveAuthToken(): no valid token after all attempts', {
        storedToken: ApiProvider.tokenDebugMeta(storedToken),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      return null;
    }
    ApiProvider.authDebug('resolveAuthToken(): falling back to localStorage token', {
      storedToken: ApiProvider.tokenDebugMeta(storedToken),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    return storedToken;
  }

  private static returnUserDataSafe(): any | null {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  private static decodeJwtPayloadSafe(token?: string | null): Record<string, any> | null {
    if (!token || typeof token !== 'string') return null;

    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const payload = JSON.parse(atob(padded));

      return payload && typeof payload === 'object' ? payload : null;
    } catch {
      return null;
    }
  }

  private static isJwtExpiredOrNearExpiry(token?: string | null, skewSeconds = 30): boolean {
    const payload = ApiProvider.decodeJwtPayloadSafe(token);
    if (!payload) return false;

    const exp = Number(payload.exp ?? NaN);
    if (!Number.isFinite(exp) || exp <= 0) {
      return false;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    return exp <= nowSeconds + Math.max(0, skewSeconds);
  }

  private static tokenMatchesUser(token: string, user: any): boolean {
    if (!token || !user) return false;

    const payload = ApiProvider.decodeJwtPayloadSafe(token);
    if (!payload) return false;

    const tokenSub = String(payload.sub ?? '').trim();
    const userSub = String(user?.cognito_sub ?? '').trim();
    if (tokenSub && userSub) {
      return tokenSub === userSub;
    }

    const tokenEmail = String(payload.email ?? payload['custom:email'] ?? '')
      .trim()
      .toLowerCase();
    const userEmail = String(user?.email ?? '')
      .trim()
      .toLowerCase();
    if (tokenEmail && userEmail) {
      return tokenEmail === userEmail;
    }

    const tokenUsername = String(payload['cognito:username'] ?? payload.username ?? '')
      .trim()
      .toLowerCase();
    if (tokenUsername && userEmail) {
      return tokenUsername === userEmail || tokenUsername.endsWith(`_${userEmail}`);
    }

    return false;
  }

  private static isUnauthorizedResponse(response: any, httpStatus?: number): boolean {
    if (httpStatus === 401) return true;

    if (!response) return false;

    if (typeof response === 'string') {
      return response.trim().toLowerCase() === 'unauthorized';
    }

    const message = String(response.message ?? response.error ?? '')
      .trim()
      .toLowerCase();
    if (message === 'unauthorized') return true;

    const statusCode = Number(response.status_code ?? response.status ?? NaN);
    return Number.isFinite(statusCode) && statusCode === 401;
  }

  private static async parseResponsePayload(response: Response): Promise<any> {
    if (response.status === 204) {
      return { success: true };
    }

    const responseClone = response.clone();

    try {
      return await response.json();
    } catch {
      if (response.status === 401) {
        return { success: false, message: 'Unauthorized' };
      }

      let text = '';
      try {
        text = await responseClone.text();
      } catch {
        text = '';
      }

      return {
        success: response.ok,
        message: text || response.statusText || 'Request failed',
        status: response.status,
      };
    }
  }

  private static async parseAndHandleAuthenticatedResponse(
    response: Response,
    options: {
      skipUnauthorizedHandling?: boolean;
      requestLabel?: string;
      redirectOnUnauthorized?: boolean;
    } = {},
  ): Promise<any> {
    const payload = await ApiProvider.parseResponsePayload(response);

    if (response.status === 401 || ApiProvider.isUnauthorizedResponse(payload, response.status)) {
      ApiProvider.authDebug(
        'parseAndHandleAuthenticatedResponse(): unauthorized response received',
        {
          requestLabel: options.requestLabel ?? null,
          httpStatus: response.status,
          payloadMessage:
            typeof payload === 'object' ? (payload?.message ?? payload?.error ?? null) : payload,
          skipUnauthorizedHandling: options.skipUnauthorizedHandling === true,
        },
      );
    }

    if (!options.skipUnauthorizedHandling && options.redirectOnUnauthorized === true) {
      ApiProvider.handleUnauthorizedResponse(payload, response.status);
    }

    if (response.ok && !ApiProvider.isUnauthorizedResponse(payload, response.status)) {
      ApiProvider.lastSessionProbeAt = Date.now();
    }

    return payload;
  }

  private static async getForcedFreshAuthToken(): Promise<string | null> {
    if (isMockAuthEnabled) {
      ApiProvider.authDebug('getForcedFreshAuthToken(): mock mode fallback');
      return localStorage.getItem('access_token');
    }

    // Use InitiateAuth REFRESH_TOKEN_AUTH (see refreshIdTokenViaInitiateAuth for
    // the reason we avoid fetchAuthSession({ forceRefresh: true }) here).
    ApiProvider.authDebug('getForcedFreshAuthToken(): attempting InitiateAuth refresh', {
      localStorageTokenBefore: ApiProvider.tokenDebugMeta(localStorage.getItem('access_token')),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    const refreshed = await ApiProvider.refreshIdTokenViaInitiateAuth();
    if (refreshed && !ApiProvider.isJwtExpiredOrNearExpiry(refreshed, 5)) {
      ApiProvider.authDebug(
        'getForcedFreshAuthToken(): InitiateAuth refresh returned valid token',
        {
          idToken: ApiProvider.tokenDebugMeta(refreshed),
          storage: ApiProvider.amplifyStorageDebugMeta(),
        },
      );
      return refreshed;
    }

    ApiProvider.authDebug('getForcedFreshAuthToken(): InitiateAuth refresh returned null/expired', {
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });

    const storedToken = localStorage.getItem('access_token');
    if (ApiProvider.isJwtExpiredOrNearExpiry(storedToken, 5)) {
      ApiProvider.authDebug('getForcedFreshAuthToken(): no valid fallback token', {
        storedToken: ApiProvider.tokenDebugMeta(storedToken),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      return null;
    }
    ApiProvider.authDebug('getForcedFreshAuthToken(): using localStorage fallback token', {
      storedToken: ApiProvider.tokenDebugMeta(storedToken),
      storage: ApiProvider.amplifyStorageDebugMeta(),
    });
    return storedToken;
  }

  private static async fetchAuthenticatedWithSingleRetry(
    requestFactory: (token: string) => Promise<Response>,
    options: {
      skipUnauthorizedHandling?: boolean;
      requestLabel?: string;
      redirectOnUnauthorized?: boolean;
    } = {},
  ): Promise<any> {
    let token = await ApiProvider.getAuthToken();
    if (!token) {
      // Right after login, concurrent session probes can race before the token
      // lands in storage. Retry briefly to avoid false unauthorized responses.
      for (const delayMs of [250, 500]) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        token = await ApiProvider.getAuthToken();
        if (token) break;
      }
    }

    if (!token) {
      ApiProvider.authDebug(
        'fetchAuthenticatedWithSingleRetry(): no token available before request',
        {
          requestLabel: options.requestLabel ?? null,
        },
      );
      if (options.redirectOnUnauthorized === true) {
        ApiProvider.handleUnauthorizedResponse({ message: 'Unauthorized' }, 401);
      }
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): sending initial request', {
      requestLabel: options.requestLabel ?? null,
      token: ApiProvider.tokenDebugMeta(token),
    });
    let response = await requestFactory(token);
    ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): initial response received', {
      requestLabel: options.requestLabel ?? null,
      httpStatus: response.status,
    });

    if (response.status === 401) {
      ApiProvider.authDebug(
        'fetchAuthenticatedWithSingleRetry(): initial 401, trying forced refresh',
        {
          requestLabel: options.requestLabel ?? null,
          originalToken: ApiProvider.tokenDebugMeta(token),
        },
      );
      const retryToken = await ApiProvider.getForcedFreshAuthToken();
      const canRetry =
        !!retryToken && (retryToken !== token || ApiProvider.isJwtExpiredOrNearExpiry(token, 5));

      ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): retry decision', {
        requestLabel: options.requestLabel ?? null,
        canRetry,
        retryToken: ApiProvider.tokenDebugMeta(retryToken),
        originalTokenNearExpiry: ApiProvider.isJwtExpiredOrNearExpiry(token, 5),
        tokenChanged: retryToken ? retryToken !== token : false,
      });
      if (canRetry) {
        response = await requestFactory(retryToken as string);
        ApiProvider.authDebug('fetchAuthenticatedWithSingleRetry(): retry response received', {
          requestLabel: options.requestLabel ?? null,
          httpStatus: response.status,
        });
      }
    }

    return ApiProvider.parseAndHandleAuthenticatedResponse(response, options);
  }

  static async ensureSessionAlive(force = false): Promise<boolean> {
    if (ApiProvider.isSessionRedirectInProgress) {
      return false;
    }

    const hasUserData = Boolean(localStorage.getItem('userData'));
    const hasAccessToken = Boolean(localStorage.getItem('access_token'));
    if (!hasUserData || !hasAccessToken) {
      return false;
    }

    const now = Date.now();
    if (!force && now - ApiProvider.lastSessionProbeAt < ApiProvider.SESSION_PROBE_INTERVAL_MS) {
      return true;
    }

    if (ApiProvider.sessionProbeInFlight) {
      return ApiProvider.sessionProbeInFlight;
    }

    ApiProvider.sessionProbeInFlight = (async () => {
      try {
        const userData = ApiProvider.returnUserDataSafe();
        const payload = await ApiProvider.fetchAuthenticatedWithSingleRetry(
          async (token) => {
            const headers: Record<string, string> = {
              'content-type': 'application/json',
              accept: 'application/json',
              Authorization: `Bearer ${token}`,
            };
            if (userData?.id) {
              headers.UserId = String(userData.id);
            }

            return fetch(ApiProvider.apiUrl + 'User/Details', {
              method: 'GET',
              headers,
            });
          },
          { requestLabel: 'User/Details (session probe)', redirectOnUnauthorized: true },
        );

        if (ApiProvider.isUnauthorizedResponse(payload)) {
          ApiProvider.authDebug(
            'ensureSessionAlive(): backend reported unauthorized after retry flow',
            {
              payloadMessage:
                typeof payload === 'object'
                  ? (payload?.message ?? payload?.error ?? null)
                  : payload,
            },
          );
          return false;
        }

        // Treat non-auth failures (network/server) as "unknown" and avoid
        // force-logging out the user. The next API call or probe can retry.
        return true;
      } catch (error: any) {
        ApiProvider.authDebug(
          'ensureSessionAlive(): probe threw non-auth error (treated as keep-session)',
          {
            name: error?.name,
            message: error?.message,
          },
        );
        return true;
      } finally {
        ApiProvider.sessionProbeInFlight = null;
      }
    })();

    return ApiProvider.sessionProbeInFlight;
  }

  static webservicePost(pageName: string, postParam: BodyInit | null): Promise<any> {
    return new Promise((resolve) => {
      fetch(this.apiUrl + pageName, {
        method: 'POST',
        body: postParam,
      })
        .then((response) => response.json())
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostTokenDelete(
    pageName: string,
    postParam: BodyInit | null,
  ): Promise<any> {
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) =>
          fetch(this.apiUrl + pageName, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(postParam),
          }),
        { requestLabel: `DELETE ${pageName}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostToken(
    pageName: string,
    postParam: BodyInit | null,
    loader: 'yes' | 'no' | null,
    signal: AbortSignal | null = null,
    options: { suppressUnauthorizedRedirect?: boolean } = {},
  ): Promise<any> {
    const userData = this.returnUserDataSafe();
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) => {
          const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`,
          };
          if (userData?.id) {
            headers.UserId = String(userData.id);
          }
          // Set Content-Type for JSON requests (string body)
          if (typeof postParam === 'string') {
            headers['Content-Type'] = 'application/json';
          }

          return fetch(this.apiUrl + pageName, {
            method: 'POST',
            headers,
            body: postParam,
            signal: signal,
          });
        },
        {
          requestLabel: `POST ${pageName}`,
          skipUnauthorizedHandling: options.suppressUnauthorizedRedirect === true,
        },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static webservicePostCustomToken(
    pageName: string,
    postParam: BodyInit | null,
    token: string,
  ): Promise<any> {
    return new Promise((resolve) => {
      fetch(this.apiUrl + pageName, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: postParam,
      })
        .then((response) => response.json())
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostTokenPut(
    pageName: string,
    postParam: BodyInit | object | null,
  ): Promise<any> {
    const userData = this.returnUserDataSafe();
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) => {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          };
          if (userData?.id) {
            headers.UserId = String(userData.id);
          }

          return fetch(this.apiUrl + pageName, {
            method: 'PUT',
            headers,
            body: JSON.stringify(postParam),
          });
        },
        { requestLabel: `PUT ${pageName}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webservicePostTokenPatch(
    pageName: string,
    postParam: BodyInit | object | null,
  ): Promise<any> {
    const userData = this.returnUserDataSafe();
    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) => {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          };
          if (userData?.id) {
            headers.UserId = String(userData.id);
          }

          return fetch(this.apiUrl + pageName, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(postParam),
          });
        },
        { requestLabel: `PATCH ${pageName}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  static async webserviceGet(
    pageName: string,
    queryParams?: string | string[][] | Record<string, string> | URLSearchParams | null,
    signal: AbortSignal | null = null,
  ): Promise<any> {
    let query: string = '';
    if (queryParams) {
      const params = new URLSearchParams(queryParams).toString();
      query = '?' + decodeURIComponent(params);
    }

    return new Promise((resolve) => {
      ApiProvider.fetchAuthenticatedWithSingleRetry(
        (token) =>
          fetch(this.apiUrl + pageName + query, {
            method: 'GET',
            headers: {
              'content-type': 'application/json',
              accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            signal: signal,
          }),
        { requestLabel: `GET ${pageName}${query}` },
      )
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          resolve(err);
        });
    });
  }

  /**
   * Called after parsing every authenticated API response.
   * If the server reports "Unauthorized" (expired/invalid/revoked JWT), clear
   * local auth state and hard-navigate to the login page so the user isn't
   * silently stuck on a broken page. We guard on `access_token` being present
   * so unauthenticated flows (login page itself) are never accidentally
   * redirected.
   */
  private static handleUnauthorizedResponse(response: any, httpStatus?: number): void {
    if (
      ApiProvider.isUnauthorizedResponse(response, httpStatus) &&
      localStorage.getItem('access_token')
    ) {
      if (ApiProvider.isSessionRedirectInProgress) {
        ApiProvider.authDebug(
          'handleUnauthorizedResponse(): redirect already in progress, ignoring duplicate unauthorized',
        );
        return;
      }
      ApiProvider.authDebug('handleUnauthorizedResponse(): triggering session-expired redirect', {
        httpStatus: httpStatus ?? null,
        payloadMessage:
          typeof response === 'object'
            ? (response?.message ?? response?.error ?? null)
            : String(response),
        currentPath:
          typeof window !== 'undefined' ? window.location.pathname + window.location.hash : null,
        localStorageToken: ApiProvider.tokenDebugMeta(localStorage.getItem('access_token')),
        storage: ApiProvider.amplifyStorageDebugMeta(),
      });
      ApiProvider.isSessionRedirectInProgress = true;

      try {
        sessionStorage.setItem(
          ApiProvider.SESSION_EXPIRED_MESSAGE_KEY,
          ApiProvider.SESSION_EXPIRED_MESSAGE_TEXT,
        );
      } catch {
        // Ignore storage failures; redirect should still proceed.
      }

      [
        'userData',
        'access_token',
        'refresh_token',
        'expires_in',
        'userRole',
        'BrandAccess',
        'brand_id',
        'brand_id_object',
        'property_id',
        'useraccess',
        'commondata',
        'permissiondata',
        'permissionsMap',
        'mediaValueTypeList',
        'filterMediaSourceId',
        'BenchPropertiesArr',
        'guessmarketvalue',
        'dbs_datasource_type',
        'dbs_upload_percent',
        'dbs_upload_id',
      ].forEach((key) => localStorage.removeItem(key));
      window.location.replace('/');
    }
  }

  static consumeSessionExpiredMessage(): string | null {
    try {
      const message = sessionStorage.getItem(ApiProvider.SESSION_EXPIRED_MESSAGE_KEY);
      if (message) {
        sessionStorage.removeItem(ApiProvider.SESSION_EXPIRED_MESSAGE_KEY);
      }
      return message;
    } catch {
      return null;
    }
  }

  /** Strip HTML tags from backend-supplied strings before displaying */
  private static sanitizeMessage(message: string): string {
    if (typeof message !== 'string') return 'An error occurred';
    // Remove HTML tags to prevent any accidental HTML injection via toast
    return (
      message
        .replace(/<[^>]*>/g, '')
        .trim()
        .slice(0, 500) || 'An error occurred'
    );
  }

  static globalToastMessage(
    message: string,
    duration: number = 5000,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
  ) {
    const safeMessage = ApiProvider.sanitizeMessage(message);
    const configs: ToastOptions = {
      position: 'top-right',
      autoClose: duration,
      theme: 'colored',
    };

    if (type == 'success') {
      toast.success(safeMessage, configs);
    } else if (type == 'error') {
      toast.error(safeMessage, configs);
    } else if (type == 'warning') {
      toast.warning(safeMessage, configs);
    } else {
      toast.info(safeMessage, configs);
    }
  }

  static globalApiErrorMessage(message: ApiErrorMessage): void {
    if (ApiProvider.isUnauthorizedResponse(message)) {
      // Unauthorized is handled centrally with a forced redirect and a single
      // post-redirect message on the login page.
      return;
    }

    if (typeof message === 'string') {
      if (message === 'Unexpected token < in JSON at position 0') {
        this.globalToastMessage('Something wrong. Please try again', 5000, 'error');
      } else {
        this.globalToastMessage(message, 5000, 'error');
      }
    } else {
      for (const key in message) {
        this.globalToastMessage(message[key][0], 5000, 'error');
      }
    }
  }

  static returnUserData() {
    return JSON.parse(localStorage.getItem('userData')!);
  }

  // get user access data
  static returnUserAccessData() {
    return JSON.parse(localStorage.getItem('useraccess')!);
  }

  static returnUserBrandOrPropertyId() {
    let brand_id = '';
    let property_id = '';
    const userData = this.returnUserData();
    if (userData.type === 3) {
      property_id = localStorage.getItem('property_id') || '';
    }
    if (userData.type === 4 || userData.type === 2) {
      brand_id = localStorage.getItem('brand_id') || '';
    }

    return removeEmpty({
      brand_id,
      property_id,
    });
  }

  static redirectToPermissionDeniedPage() {
    const newHref = window.location.href.split('#');
    window.location.href = newHref[0] + '#/nopermission';
  }

  static sendToNewPage() {
    setTimeout(() => {
      window.location.reload();
    }, 200);
  }

  static displayDateFormat(date: string) {
    if (date == '' || date == null || date == undefined) {
      return '';
    }
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'June',
      'July',
      'Aug',
      'Sept',
      'Oct',
      'Nov',
      'Dec',
    ];
    const d = new Date(date);
    const month = months[d.getMonth()];
    const getDate = d.getDate();
    const getFullYear = d.getFullYear();
    return getDate + ' ' + month + ' ' + getFullYear;
  }

  static downloadFile(
    document_media_id: number,
    media_id: number,
    module_type: string,
    displayType: string,
  ) {
    const body = new FormData();
    if (module_type == 'document') {
      body.append('document_media_id', String(document_media_id));
    } else {
      body.append('media_ids[]', String(media_id));
    }
    body.append('download_type', module_type);
    body.append('local_path', 'true');
    this.webservicePostToken('DocumentMedia/DownloadDocumentMedia', body, 'yes').then(
      (response: any) => {
        if (response.success == true) {
          const baseurl = this.apiUrl.split('/mservices/');
          const fileUrl =
            baseurl[0] + '/mservices/contract/storage/app/' + response.data.local_path;
          if (displayType == 'view') {
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
          } else {
            new JsFileDownloader({
              url: fileUrl,
            })
              .then(function () {
                // Called when download ended
              })
              .catch(function () {
                // Called when an error occurred
              });
          }
        } else {
          this.globalApiErrorMessage(response.message);
        }
      },
      (error) => {
        this.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  }

  // Currency functions start
  static filterCurrency() {
    let currencyString = '';

    const localStorageFilters: { filter_type: string }[] = JSON.parse(
      localStorage.getItem('globalsearch') || '[]',
    );

    if (localStorageFilters) {
      const currencyItem = localStorageFilters.find(
        (item) => item.filter_type == 'currency',
      ) as GlobalCurrencyFilterDTO;
      currencyString = currencyItem
        ? currencyItem.currency_code
        : this.getCurrencyValueFromCommonData();
    }

    if (!currencyString) {
      currencyString = this.returnUserData()?.currency?.currency_code;
    }

    return currencyString;
  }

  static getCurrencyValueFromCommonData() {
    const commonData = ApiProvider.getMasterData();
    if (commonData && commonData.comman.currency) {
      const currency_id = ApiProvider.returnUserData()?.currency_id;
      const currencyItem = commonData.comman.currency.find((item: any) => item.id == currency_id);
      return currencyItem ? currencyItem.currency_code : '';
    }
    return '';
  }

  static userCurrency() {
    let currencyValue = '';
    const commonData = ApiProvider.getMasterData();
    if (commonData != '') {
      if (commonData.comman.currency) {
        const currency = commonData.comman.currency;

        const currency_id = ApiProvider.returnUserData()?.currency_id;
        let filteredItens = currency;
        filteredItens = filteredItens.filter((item: any) => {
          return item.id == currency_id;
        });
        if (filteredItens.length > 0) {
          currencyValue = filteredItens[0].currency_code + ' (' + filteredItens[0].symbol + ')';
        }
      }
    }
    return currencyValue;
  }

  static currencyConvertor(val: number) {
    if (!val && val !== 0) return ''; // Return empty string for invalid or undefined values
    const hasDecimals = val % 1 !== 0; // Check if there are decimals
    return hasDecimals ? val.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') : val.toLocaleString();
  }

  // Currency functions end

  // Get Master Common Data start
  static async getMasterCommonData(modulename: string) {
    const parameters: Record<string, string> = { flag: 'active' };
    if (modulename != '') {
      parameters.module_type = modulename;
    }

    const response = (await this.webserviceGet(
      'GetMasterCommanData',
      parameters,
    )) as ResponseDTO<any>;

    if (!response || !response.success) return;

    localStorage.setItem('commondata', JSON.stringify(response.data));
  }

  static getMasterData() {
    const commonData = localStorage.getItem('commondata');

    if (!commonData) return '';

    return JSON.parse(commonData);
  }

  static exchange_rates_text() {
    return 'Please contact system administrator to add the exchange rates';
  }

  // Permission
  static async fetchPermissions() {
    try {
      const response = await ApiProvider.webserviceGet('User/PermissionsMap');
      if (!response.success) throw new Error(response.message);

      localStorage.setItem('permissionsMap', JSON.stringify(response.data));
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  // Get Media Value Type
  static async getMediaValueType() {
    try {
      const response = await ApiProvider.webserviceGet('Activation/User/GetMediaValueType');
      if (!response.success) throw new Error(response.message);
      localStorage.setItem('mediaValueTypeList', JSON.stringify(response.data));
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  static permission(permissionName: string): boolean {
    try {
      const localData = localStorage.getItem('permissionsMap');

      if (!localData) return false;

      const permissionsMap: Record<string, boolean> = JSON.parse(localData);

      return permissionsMap[permissionName];
    } catch (error: any) {
      console.error(error.message);

      return false;
    }
  }

  static getRandomColor() {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  static getRandomColorByIndex(index: number) {
    const colorArray = [
      '#004D79',
      '#C66FBD',
      '#007969',
      '#E69400',
      '#79004D',
      '#7979C3',
      '#FF7A5B',
      '#2C0079',
      '#3679AB',
      '#CFB86A',
      '#677D92',
    ];
    let colorCode = '';
    if (colorArray[index]) {
      colorCode = colorArray[index];
    } else {
      colorCode = ApiProvider.getRandomColor();
    }
    return colorCode;
  }

  static returnMediaValueList() {
    try {
      const data = localStorage.getItem('mediaValueTypeList');
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  /* ------------------ AUTHENTICATION ------------------ */

  static async completePendingChatLinkIfAny(): Promise<{
    attempted: boolean;
    success: boolean;
    message?: string;
    code?: number;
    data?: any;
  }> {
    const pending = getPendingChatLink();
    if (!pending) {
      return { attempted: false, success: false };
    }

    const body = new FormData();
    body.append('link_id', pending.linkId);
    body.append('state', pending.state);

    const response = await ApiProvider.webservicePostToken('chat/link/complete', body, 'no');
    const code = Number(response?.code ?? response?.status ?? NaN);
    const successMessage =
      typeof response?.message === 'string' && response.message.trim()
        ? response.message.trim()
        : 'Chat account linked successfully';
    const errorMessage =
      typeof response?.message === 'string' && response.message.trim()
        ? response.message.trim()
        : 'Unable to complete chat link';

    if (response?.success) {
      clearPendingChatLink();
      return {
        attempted: true,
        success: true,
        message: successMessage,
        data: response?.data,
      };
    }

    // Terminal errors: invalid/expired/used/malformed link. Clear the local copy
    // so we do not keep retrying the same link on every login.
    if ([409, 410, 422].includes(code)) {
      clearPendingChatLink();
    }

    return {
      attempted: true,
      success: false,
      message: errorMessage,
      code: Number.isFinite(code) ? code : undefined,
      data: response?.data,
    };
  }

  static async signInUserWithUserData(userData: any, message: string) {
    localStorage.setItem('userData', JSON.stringify(userData.user));

    let resolvedAccessToken =
      typeof userData?.access_token === 'string' ? userData.access_token.trim() : '';
    const hasResponseToken =
      resolvedAccessToken !== '' &&
      resolvedAccessToken !== 'undefined' &&
      resolvedAccessToken !== 'null';

    let cognitoIdToken = '';
    // In mock mode there is no real Cognito session — skip fetchAuthSession() entirely
    // so a stale Cognito IdToken never replaces the mock JWT stored by the backend.
    if (!isMockAuthEnabled) {
      try {
        const session = await fetchAuthSession();
        cognitoIdToken = session.tokens?.idToken?.toString?.() || '';
      } catch {
        // Ignore session-read failures. We'll fall back to the login response token.
      }
    }

    if (cognitoIdToken && !ApiProvider.isJwtExpiredOrNearExpiry(cognitoIdToken, 5)) {
      const responseTokenMatchesUser =
        hasResponseToken && userData?.user
          ? ApiProvider.tokenMatchesUser(resolvedAccessToken, userData.user)
          : false;
      const cognitoTokenMatchesUser = userData?.user
        ? ApiProvider.tokenMatchesUser(cognitoIdToken, userData.user)
        : true;

      // Prefer the active Cognito IdToken when response token is missing/invalid
      // or appears tied to a different user during fast account switching.
      if ((!hasResponseToken || !responseTokenMatchesUser) && cognitoTokenMatchesUser) {
        resolvedAccessToken = cognitoIdToken;
      } else if (!hasResponseToken || !responseTokenMatchesUser) {
        ApiProvider.authDebug(
          'signInUserWithUserData(): keeping response token because Amplify token mismatched user',
          {
            responseToken: ApiProvider.tokenDebugMeta(
              hasResponseToken ? resolvedAccessToken : undefined,
            ),
            cognitoIdToken: ApiProvider.tokenDebugMeta(cognitoIdToken),
            responseTokenMatchesUser,
            cognitoTokenMatchesUser,
            userId: userData?.user?.id ?? null,
            userEmail: userData?.user?.email ?? null,
          },
        );
      }
    }

    if (
      !resolvedAccessToken ||
      resolvedAccessToken === 'undefined' ||
      resolvedAccessToken === 'null'
    ) {
      throw new Error('Unable to establish authenticated session token');
    }

    localStorage.setItem('access_token', resolvedAccessToken);
    if (userData.refresh_token) {
      localStorage.setItem('refresh_token', userData.refresh_token);
    } else {
      localStorage.removeItem('refresh_token');
    }
    localStorage.setItem('expires_in', userData.expires_in);
    localStorage.removeItem('brand_id');
    localStorage.removeItem('brand_id_object');
    localStorage.removeItem('globalsearch');

    const defaultFilter = userData.default_filter?.[0]?.filters_comination;
    if (defaultFilter) {
      localStorage.setItem('globalsearch', defaultFilter);
    }

    await this.fetchPermissions();

    await this.getMediaValueType();

    this.get_user_access();
    this.getUserAccessRole(userData.user.id);

    await Promise.all([this.getUserAccessBrand(userData.user.id), this.getMasterCommonData('')]);

    if (userData.user.type == 3) {
      await this.getUserAccessProperty();
    }

    //--login redirection with user permission
    if (message) {
      this.globalToastMessage(message, 5000, 'success');
    }

    return userData.user;
  }

  static get_user_access() {
    this.webserviceGet('User/Details').then(
      (response: any) => {
        if (response.success == true) {
          localStorage.setItem('useraccess', JSON.stringify(response.data));
        }
      },
      (error) => {
        this.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  }

  static getUserAccessRole(user_id: number) {
    ApiProvider.webserviceGet('User/Roles?user_id=' + user_id).then(
      (response: any) => {
        if (response.success == true) {
          localStorage.setItem('userRole', JSON.stringify(response.data));
        } else {
          localStorage.removeItem('userRole');
        }
      },
      (error) => {
        ApiProvider.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  }

  static async getUserAccessBrand(user_id: number) {
    try {
      const response: any = await ApiProvider.webserviceGet('User/Brands?user_id=' + user_id);
      if (!response.success) return localStorage.removeItem('BrandAccess');
      localStorage.setItem('BrandAccess', JSON.stringify(response.data));
      if (response.data.length == 1) {
        if (response.data[0] != '*') {
          localStorage.setItem('brand_id', response.data[0]);
          getBrandListById(response.data[0])
            .then((data) => {
              localStorage.setItem('brand_id_object', JSON.stringify(data.results[0]));
            })
            .catch((error) => {
              console.log(error);
              // Clear brand array on error
            });
        }
      }
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  static async getUserAccessProperty() {
    try {
      const response: any = await ApiProvider.webservicePostToken(
        'Property/GetPropertyList',
        null,
        'yes',
      );

      if (!response.success) return;

      if (response.data.results.length === 1) {
        localStorage.setItem('property_id', response.data.results[0].id);
        localStorage.setItem('property_data', JSON.stringify(response.data.results[0]));
      }
    } catch (error: any) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
      return;
    }
  }

  // This function is used to redirect logged in user to their respective dashboard
  static getRedirectionUrl(user: { type: number }): string {
    try {
      const sessionURL = localStorage.getItem('sessionurl');
      if (sessionURL) {
        const hashIndex = sessionURL.indexOf('#/');
        if (hashIndex >= 0) {
          const hashPath = sessionURL
            .substring(hashIndex + 2)
            .split('?')[0]
            .split('#')[0];
          const urlName = hashPath.replace(/^\/+/, '');
          const blockedRoutes = new Set([
            '',
            'dashboard',
            'sso',
            'resetpassword',
            'login',
            'undefined',
            'null',
          ]);
          if (!blockedRoutes.has(urlName)) {
            return `/${urlName}`;
          }
        }
      }

      const userType = Number(user?.type);
      const userTypes = {
        global: 1,
        brand: 2,
        property: 3,
        agency: 4,
      };

      const brandAccessValue = localStorage.getItem('BrandAccess');
      const userBrandAccess = brandAccessValue ? JSON.parse(brandAccessValue) : null;

      const propertyAccessValue = localStorage.getItem('property_id');
      const propertyAccessId = propertyAccessValue ? JSON.parse(propertyAccessValue) : null;

      let redirectionUrl = '/dashboard';
      switch (userType) {
        case userTypes.brand:
          if (
            Array.isArray(userBrandAccess) &&
            userBrandAccess[0] != '*' &&
            userBrandAccess.length == 1
          ) {
            redirectionUrl = '/brandprofile/' + userBrandAccess[0];
          }
          break;
        case userTypes.property:
          if (propertyAccessId) {
            redirectionUrl = `/partnerProfile/${propertyAccessId}`;
          }
          break;
        default:
          redirectionUrl = '/dashboard';
          break;
      }

      return redirectionUrl;
    } catch (error) {
      return '/dashboard';
    }
  }

  static addEllipsis(str: string, maxLength: number) {
    if (str.length > maxLength) {
      // Truncate the string to the specified maxLength
      str = str.substring(0, maxLength) + '...';
    }
    return str;
  }

  static formatToSB(value: number) {
    const format = (num: number, suffix: string) =>
      Number.isInteger(num) ? num + suffix : num.toFixed(2) + suffix;

    if (value >= 1_000_000_000_000) {
      return format(value / 1_000_000_000_000, 'T'); // Trillion
    } else if (value >= 1_000_000_000) {
      return format(value / 1_000_000_000, 'B'); // Billion
    } else if (value >= 1_000_000) {
      return format(value / 1_000_000, 'M'); // Million
    } else if (value >= 1_000) {
      return format(value / 1_000, 'K'); // Thousand
    }
    return value.toString();
  }

  /* ------------------ PROPERTY NODE API ------------------ */

  static getPropertyNodeUrl(): string {
    return `${String(this.apiUrl).replace(/\/+$/, '')}/property-node`;
  }

  private static getPropertyNodeHeaders(contentType = true): Record<string, string> {
    const headers: Record<string, string> = {
      accept: 'application/json',
    };

    if (contentType) {
      headers['Content-Type'] = 'application/json';
    }

    const token = localStorage.getItem('access_token');
    const userData = this.returnUserDataSafe();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (userData?.id) {
      headers.UserId = String(userData.id);
    }

    return headers;
  }

  private static normalizePropertyNodeEndpoint(endpoint: string): string {
    if (!endpoint) return '';
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }

  static async propertyNodeGet<T = any>(
    endpoint: string,
    queryParams?: Record<string, string | number> | URLSearchParams | string | null,
    signal: AbortSignal | null = null,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    let query = '';
    if (queryParams) {
      const params =
        typeof queryParams === 'string'
          ? queryParams.replace(/^\?/, '')
          : queryParams instanceof URLSearchParams
            ? queryParams.toString()
            : new URLSearchParams(
                Object.fromEntries(Object.entries(queryParams).map(([k, v]) => [k, String(v)])),
              ).toString();
      query = params ? `?${params}` : '';
    }

    try {
      const response = await fetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}${query}`,
        {
          method: 'GET',
          headers: this.getPropertyNodeHeaders(),
          signal: signal ?? undefined,
        },
      );

      const payload = await this.parseResponsePayload(response);

      // Property Node API wraps responses in { success, data, message }
      return payload;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static async propertyNodePost<T = any>(
    endpoint: string,
    body?: any,
    signal: AbortSignal | null = null,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    try {
      const response = await fetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}`,
        {
          method: 'POST',
          headers: this.getPropertyNodeHeaders(),
          body: body ? JSON.stringify(body) : undefined,
          signal: signal ?? undefined,
        },
      );

      const payload = await this.parseResponsePayload(response);

      // Property Node API wraps responses in { success, data, message }
      return payload;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static async propertyNodePut<T = any>(
    endpoint: string,
    body?: any,
    signal: AbortSignal | null = null,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    try {
      const response = await fetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}`,
        {
          method: 'PUT',
          headers: this.getPropertyNodeHeaders(),
          body: body ? JSON.stringify(body) : undefined,
          signal: signal ?? undefined,
        },
      );

      const payload = await this.parseResponsePayload(response);

      return payload;
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  static async propertyNodeDelete<T = any>(
    endpoint: string,
  ): Promise<{ success: boolean; data?: T; message?: string; status?: number }> {
    const token = await this.getAuthToken();
    if (!token) {
      return { success: false, message: 'Unauthorized', status: 401 };
    }

    try {
      const response = await fetch(
        `${this.getPropertyNodeUrl()}${this.normalizePropertyNodeEndpoint(endpoint)}`,
        {
          method: 'DELETE',
          headers: this.getPropertyNodeHeaders(),
        },
      );

      return await this.parseResponsePayload(response);
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Network error',
        status: 0,
      };
    }
  }

  /* ------------------ AUTHENTICATION END ------------------ */

  static async logoutUserLocally(navigationStatus: any, options: { globalSignOut?: boolean } = {}) {
    const shouldGlobalSignOut = options.globalSignOut === true;

    // Clear all localStorage FIRST — before signOut, because with OAuth
    // configured signOut() triggers an immediate browser redirect to Cognito's
    // /logout endpoint. Any code after `await signOut()` never runs in that
    // case. Clearing here (before the first await) runs synchronously and
    // ensures auth state is clean when the browser lands back at the app.
    localStorage.removeItem('userData');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('userRole');
    localStorage.removeItem('BrandAccess');
    localStorage.removeItem('brand_id');
    localStorage.removeItem('brand_id_object');
    localStorage.removeItem('property_id');
    localStorage.removeItem('property_data');
    localStorage.removeItem('useraccess');
    localStorage.removeItem('dbs_datasource_type');
    localStorage.removeItem('dbs_upload_percent');
    localStorage.removeItem('dbs_upload_id');
    localStorage.removeItem('commondata');
    localStorage.removeItem('BenchPropertiesArr');
    localStorage.removeItem('permissiondata');
    localStorage.removeItem('permissionsMap');
    localStorage.removeItem('mediaValueTypeList');
    localStorage.removeItem('guessmarketvalue');
    localStorage.removeItem('filterMediaSourceId');

    // Capture current location before signOut so we can detect whether
    // Amplify's signOut() initiated a browser redirect (OAuth /logout flow).
    // signOut() calls window.location.assign() synchronously inside its async
    // body, which updates window.location.href immediately even though the
    // page hasn't loaded yet. If the href changed, the browser is already
    // navigating — running window.location.href = '/' afterwards would race
    // with that redirect and cause a double page-load (the "constantly
    // refreshing login page" bug seen after password-user logout in an
    // OAuth-configured app).
    const hrefBeforeSignOut = window.location.href;

    if (!isMockAuthEnabled) {
      // Default logout should sign out only the current device/session.
      // Use `{ globalSignOut: true }` for explicit "sign out all devices".
      // With OAuth configured, either path may redirect via Cognito /logout.
      try {
        if (shouldGlobalSignOut) {
          await signOut({ global: true });
        } else {
          await signOut();
        }
      } catch (error) {
        console.error('Cognito sign out error:', error);
      }
    }

    // Only navigate manually when signOut() did NOT already start a browser
    // redirect (i.e. non-OAuth environments, mock mode, or when the OAuth
    // redirect was suppressed by an error). If Amplify's signOut() changed
    // the location we let that navigation complete and skip this fallback.
    if (navigationStatus && window.location.href === hrefBeforeSignOut) {
      window.location.href = '/';
    }

    return;
  }
}
